import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ListIcon from "@mui/icons-material/List";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import LocationDisabledIcon from "@mui/icons-material/LocationDisabled";
import PeopleIcon from "@mui/icons-material/People";
import AvlMap from "./AvlMap";
import { AuthContext } from "../context/AuthContext";
import { useAnnouncement } from "../context/AnnouncementContext";
import { getTenCodeLabel } from "../constants/preferences";
import { INCIDENT_TYPES } from "../constants/incidentOptions";

const STATUS_BUTTONS = [
  { label: "EMERGENCY", value: "emergency", color: "error" },
  { label: "OUT OF SERVICE", value: "out-of-service", color: "inherit" },
  { label: "AVAILABLE", value: "available", color: "success" },
  { label: "BUSY", value: "busy", color: "warning" },
];

const FOLLOW_ME_STORAGE_KEY = "mapview-follow-me";
const FOLLOW_PAUSE_DURATION_MS = 60000; // 60 seconds

function MapCenteredDashboard({
  // Map data
  avlMapUnits,
  avlMapIncidents,
  avlMapWazeAlerts,
  avlMapAbandonedVehicles,
  avlMapDestination,
  userEnroute,
  enrouteUnitId,
  isDarkMode,
  onIncidentClick,
  // Status
  unitStatus,
  onStatusChange,
  onEmergency,
  currentActiveIncident,
  // Emergency self-cancel
  activeEmergencies,
  onSelfCancelOpen,
  // Incidents
  activeIncidents,
  onQuickCreateIncident,
  onServiceCall,
  newIncidentSubmitting,
  // User info
  userOnScene,
  gpsCoords,
  // Waze alert handling
  filteredWazeAlerts,
  onWazeOnScene,
  onWazeUtl,
  wazeClaimingId,
  // Active units for sidebar list
  activeUnitsForList,
  // Sidebar state from parent
  sidebarCollapsed,
  // Tablet mode for larger touch targets
  tabletMode = false,
}) {
  const theme = useTheme();
  const { user } = useContext(AuthContext);
  const { snowbankEnabled } = useAnnouncement();
  const useTenCodes = user?.useTenCodes || false;
  
  const [incidentListExpanded, setIncidentListExpanded] = useState(false);
  const [unitsListExpanded, setUnitsListExpanded] = useState(false);
  
  // Follow me state
  const [followMeEnabled, setFollowMeEnabled] = useState(() => {
    if (typeof window === "undefined") return true;
    const saved = window.localStorage.getItem(FOLLOW_ME_STORAGE_KEY);
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [followMePaused, setFollowMePaused] = useState(false);
  const followMePauseTimerRef = useRef(null);

  // Save follow me preference
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(FOLLOW_ME_STORAGE_KEY, JSON.stringify(followMeEnabled));
    }
  }, [followMeEnabled]);

  // Clear timer on unmount
  useEffect(() => {
    return () => {
      if (followMePauseTimerRef.current) {
        clearTimeout(followMePauseTimerRef.current);
      }
    };
  }, []);

  const handleToggleFollowMe = useCallback(() => {
    setFollowMeEnabled((prev) => !prev);
    setFollowMePaused(false);
    if (followMePauseTimerRef.current) {
      clearTimeout(followMePauseTimerRef.current);
      followMePauseTimerRef.current = null;
    }
  }, []);

  const handleManualMapInteraction = useCallback(() => {
    if (!followMeEnabled) return;
    
    setFollowMePaused(true);
    
    // Clear any existing timer
    if (followMePauseTimerRef.current) {
      clearTimeout(followMePauseTimerRef.current);
    }
    
    // Resume after 60 seconds
    followMePauseTimerRef.current = setTimeout(() => {
      setFollowMePaused(false);
      followMePauseTimerRef.current = null;
    }, FOLLOW_PAUSE_DURATION_MS);
  }, [followMeEnabled]);

  const handleQuickIncident = useCallback((type) => {
    onQuickCreateIncident?.(type);
  }, [onQuickCreateIncident]);

  const handleStatusClick = useCallback((value) => {
    if (value === "emergency") {
      onEmergency?.();
    } else if (value === "available" && currentActiveIncident) {
      onIncidentClick?.(currentActiveIncident.id);
    } else {
      onStatusChange?.(value, { manual: true });
    }
  }, [onEmergency, onStatusChange, currentActiveIncident, onIncidentClick]);

  // Calculate left offset based on sidebar state
  const leftOffset = sidebarCollapsed ? 16 : 296; // 280px drawer + 16px padding

  // User position for map follow
  const userPosition = useMemo(() => {
    if (!gpsCoords?.latitude || !gpsCoords?.longitude) return null;
    return [gpsCoords.latitude, gpsCoords.longitude];
  }, [gpsCoords]);

  // Effective follow state (enabled AND not paused)
  const effectiveFollowEnabled = followMeEnabled && !followMePaused && !!userPosition;

  // Find nearby Waze alerts (within 0.25 miles)
  const nearbyWazeAlerts = useMemo(() => {
    if (!filteredWazeAlerts || !gpsCoords?.latitude || !gpsCoords?.longitude) return [];
    return filteredWazeAlerts.filter((alert) => 
      alert.distanceMiles !== null && alert.distanceMiles <= 0.25 && alert.status === "active"
    );
  }, [filteredWazeAlerts, gpsCoords]);

  // Get waze alert type label
  const getWazeTypeLabel = (alert) => {
    const subtypeUpper = alert.subtype?.toUpperCase() || "";
    const isCarStopped = subtypeUpper.includes("CAR_STOPPED");
    const isEmergencyVehicle = subtypeUpper.includes("EMERGENCY_VEHICLE");
    
    if (isEmergencyVehicle) return "ASSIST OTHER AGENCY";
    if (isCarStopped) return "ASSIST MOTORIST";
    if (alert.type === "HAZARD") return "DEBRIS";
    return alert.type?.replace(/_/g, " ") || "UNKNOWN";
  };

  // Incident types (excluding test)
  const incidentTypes = INCIDENT_TYPES.filter((t) => {
    const normalized = t.toLowerCase();
    return normalized !== "test" && normalized !== "assist other agency";
  });

  // Common button styles - all same width based on longest text
  const buttonBaseStyles = {
    textTransform: "uppercase",
    fontWeight: 700,
    fontSize: tabletMode ? "1.05rem" : "0.8rem",
    minWidth: tabletMode ? 200 : 160, // Wide enough for "ASSIST OTHER AGENCY"
    whiteSpace: "nowrap",
  };

  // Status buttons - shorter height
  const statusButtonStyles = {
    ...buttonBaseStyles,
    px: tabletMode ? 3 : 2,
    py: tabletMode ? 1.5 : 0.75,
  };

  // Incident buttons - taller for easier tapping
  const incidentButtonStyles = {
    ...buttonBaseStyles,
    px: tabletMode ? 3 : 2,
    py: tabletMode ? 3 : 2,
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 64, // AppBar height
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Full-screen Map */}
      <Box sx={{ flex: 1, position: "relative", height: "100%" }}>
        <AvlMap
          units={avlMapUnits}
          incidents={avlMapIncidents}
          wazeAlerts={avlMapWazeAlerts}
          abandonedVehicles={avlMapAbandonedVehicles}
          destination={avlMapDestination}
          enrouteUnitId={enrouteUnitId}
          tabletMode={tabletMode}
          isDarkMode={isDarkMode}
          onIncidentClick={onIncidentClick}
          height="100%"
          readOnly={false}
          userPosition={userPosition}
          followEnabled={effectiveFollowEnabled}
          onManualInteraction={handleManualMapInteraction}
          enlargedMarkers={true}
        />

        {/* Follow Me Toggle - Top Right below reset button */}
        <Paper
          elevation={4}
          sx={{
            position: "absolute",
            top: 56,
            right: 16,
            zIndex: 1000,
            backgroundColor: theme.palette.mode === "dark" ? "rgba(18, 18, 18, 0.95)" : "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(8px)",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Button
            onClick={handleToggleFollowMe}
            variant={followMeEnabled ? "contained" : "outlined"}
            color={followMeEnabled ? (followMePaused ? "warning" : "primary") : "inherit"}
            startIcon={followMeEnabled ? <MyLocationIcon /> : <LocationDisabledIcon />}
            sx={{
              fontWeight: 700,
              textTransform: "uppercase",
              fontSize: tabletMode ? "0.95rem" : "0.75rem",
              px: tabletMode ? 2.5 : 1.5,
              py: tabletMode ? 1.25 : 0.75,
            }}
          >
            {followMeEnabled 
              ? (followMePaused ? "PAUSED" : "FOLLOW") 
              : "FOLLOW OFF"}
          </Button>
        </Paper>

        {/* Status & Incident Buttons - Top Center */}
        <Paper
          elevation={6}
          sx={{
            position: "absolute",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1000,
            px: tabletMode ? 3 : 2,
            py: tabletMode ? 2.5 : 1.5,
            backgroundColor: theme.palette.mode === "dark" ? "rgba(18, 18, 18, 0.95)" : "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(8px)",
            borderRadius: 2,
            width: "auto",
            maxWidth: "90vw",
            overflow: "hidden",
            border: snowbankEnabled ? "2px solid rgba(79, 195, 247, 0.6)" : "none",
            // Snowbank effect - snow on top of the container
            ...(snowbankEnabled && {
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "14px",
                background: "linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(220,235,250,0.8) 40%, rgba(200,220,245,0.4) 70%, transparent 100%)",
                borderRadius: "8px 8px 50% 50% / 8px 8px 100% 100%",
                filter: "blur(1px)",
                zIndex: 10,
                pointerEvents: "none",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                top: "-4px",
                left: "5%",
                right: "5%",
                height: "20px",
                background: "radial-gradient(ellipse at 20% 50%, rgba(255,255,255,0.9) 0%, transparent 50%), radial-gradient(ellipse at 50% 40%, rgba(255,255,255,0.95) 0%, transparent 45%), radial-gradient(ellipse at 80% 50%, rgba(255,255,255,0.9) 0%, transparent 50%)",
                filter: "blur(2px)",
                zIndex: 11,
                pointerEvents: "none",
              },
            }),
          }}
        >
          <Stack spacing={1.5}>
            {/* Status Row */}
            <Stack direction="row" spacing={1} justifyContent="center">
              {STATUS_BUTTONS.map((button) => (
                <Button
                  key={button.value}
                  variant={unitStatus === button.value ? "contained" : "outlined"}
                  color={button.color}
                  onClick={() => handleStatusClick(button.value)}
                  sx={statusButtonStyles}
                >
                  {getTenCodeLabel(button.value, useTenCodes) || button.label}
                </Button>
              ))}
            </Stack>

            {/* Incident Row - taller buttons for easier tapping */}
            <Stack direction="row" spacing={1} justifyContent="center">
              {incidentTypes.map((type) => (
                <Button
                  key={type}
                  variant="contained"
                  color="primary"
                  onClick={() => handleQuickIncident(type)}
                  disabled={newIncidentSubmitting}
                  sx={incidentButtonStyles}
                >
                  {getTenCodeLabel(type, useTenCodes) || type}
                </Button>
              ))}
              <Button
                variant="contained"
                color="secondary"
                onClick={onServiceCall}
                disabled={newIncidentSubmitting}
                sx={incidentButtonStyles}
              >
                Service Call
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* Your Emergency Active Banner */}
        {Array.isArray(activeEmergencies) && activeEmergencies.some((e) => e.userId === user?.id) && (
          <Paper
            elevation={6}
            sx={{
              position: "absolute",
              top: 140,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1001,
              px: 2,
              py: 1.5,
              backgroundColor: "rgba(255, 152, 0, 0.95)",
              borderRadius: 2,
              textAlign: "center",
              animation: "emergencyFlash 1.5s ease-in-out infinite",
              "@keyframes emergencyFlash": {
                "0%, 100%": { boxShadow: "0 0 12px rgba(255,152,0,0.8)" },
                "50%": { boxShadow: "0 0 4px rgba(255,152,0,0.3)" },
              },
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ color: "white", fontWeight: 900, textTransform: "uppercase", letterSpacing: 1, mb: 0.5 }}
            >
              ⚠️ YOUR EMERGENCY IS ACTIVE
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={onSelfCancelOpen}
              sx={{
                backgroundColor: "white",
                color: "#e65100",
                fontWeight: 700,
                textTransform: "uppercase",
                "&:hover": { backgroundColor: "#f5f5f5" },
              }}
            >
              CANCEL MY EMERGENCY
            </Button>
          </Paper>
        )}

        {/* Nearby Waze Alert Panel - Shows when within 0.25 miles */}
        {nearbyWazeAlerts.length > 0 && (
          <Paper
            elevation={6}
            sx={{
              position: "absolute",
              bottom: 24,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1000,
              px: 2,
              py: 1.5,
              backgroundColor: theme.palette.mode === "dark" ? "rgba(18, 18, 18, 0.95)" : "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(8px)",
              borderRadius: 2,
              maxWidth: "90vw",
              animation: "pulse 2s ease-in-out infinite",
              "@keyframes pulse": {
                "0%, 100%": { boxShadow: `0 0 0 0 ${theme.palette.warning.main}40` },
                "50%": { boxShadow: `0 0 0 8px ${theme.palette.warning.main}00` },
              },
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", color: theme.palette.warning.main, display: "block", mb: 1 }}>
              🚨 NEARBY WAZE ALERT{nearbyWazeAlerts.length > 1 ? "S" : ""}
            </Typography>
            <Stack spacing={1.5}>
              {nearbyWazeAlerts.slice(0, 3).map((alert) => {
                const isClaiming = wazeClaimingId === alert.id;
                return (
                  <Box key={alert.id}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {getWazeTypeLabel(alert)} • {alert.distanceMiles?.toFixed(2)} mi
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block", opacity: 0.7, mb: 1, fontSize: "0.7rem" }}>
                      {alert.street || "Unknown location"}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        color="success"
                        size={tabletMode ? "medium" : "small"}
                        disabled={isClaiming}
                        onClick={() => onWazeOnScene?.(alert)}
                        sx={{ 
                          fontWeight: 700, 
                          textTransform: "uppercase",
                          flex: 1,
                          py: tabletMode ? 2 : 1.5,
                          fontSize: tabletMode ? "0.95rem" : undefined,
                        }}
                      >
                        {isClaiming ? "..." : "ON SCENE"}
                      </Button>
                      <Button
                        variant="outlined"
                        color="inherit"
                        size={tabletMode ? "medium" : "small"}
                        disabled={isClaiming}
                        onClick={() => onWazeUtl?.(alert.id)}
                        sx={{ 
                          fontWeight: 700, 
                          textTransform: "uppercase",
                          flex: 1,
                          py: tabletMode ? 2 : 1.5,
                          fontSize: tabletMode ? "0.95rem" : undefined,
                        }}
                      >
                        {isClaiming ? "..." : "UTL"}
                      </Button>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        )}

        {/* Active Incidents Panel - Right Side (below follow button) */}
        <Paper
          elevation={6}
          sx={{
            position: "absolute",
            top: 110, // Below follow button
            right: 16,
            zIndex: 999, // Below map controls
            minWidth: tabletMode ? 300 : 240,
            maxWidth: tabletMode ? 400 : 320,
            maxHeight: "calc(100vh - 250px)",
            backgroundColor: theme.palette.mode === "dark" ? "rgba(18, 18, 18, 0.95)" : "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(8px)",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: tabletMode ? 2.5 : 2,
              py: tabletMode ? 1.5 : 1,
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              cursor: "pointer",
            }}
            onClick={() => setIncidentListExpanded(!incidentListExpanded)}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <ListIcon fontSize={tabletMode ? "medium" : "small"} />
              <Typography variant={tabletMode ? "subtitle1" : "subtitle2"} sx={{ fontWeight: 700, textTransform: "uppercase" }}>
                Active ({activeIncidents?.length || 0})
              </Typography>
            </Stack>
            <IconButton size={tabletMode ? "medium" : "small"} sx={{ color: "inherit" }}>
              {incidentListExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={incidentListExpanded}>
            <Box sx={{ maxHeight: "50vh", overflowY: "auto" }}>
              {activeIncidents?.length === 0 ? (
                <Typography variant="body2" sx={{ p: 2, textAlign: "center", opacity: 0.7 }}>
                  No active incidents
                </Typography>
              ) : (
                <Stack spacing={tabletMode ? 1 : 0.5} sx={{ p: tabletMode ? 1.5 : 1 }}>
                  {activeIncidents?.slice(0, 15).map((inc) => (
                    <Paper
                      key={inc.id}
                      variant="outlined"
                      sx={{
                        p: tabletMode ? 1.5 : 1,
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                      onClick={() => onIncidentClick?.(inc.id)}
                    >
                      <Typography variant={tabletMode ? "body2" : "caption"} sx={{ fontWeight: 700, textTransform: "uppercase" }}>
                        {inc.cadId || `#${inc.id}`} • {inc.incidentType}
                      </Typography>
                      <Typography variant="caption" sx={{ display: "block", opacity: 0.7, fontSize: tabletMode ? 12 : 10 }}>
                        {inc.locationDescription || inc.streetName || "—"}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>
          </Collapse>
        </Paper>

        {/* On Scene Indicator - Top Left (offset by sidebar) */}
        {userOnScene && (
          <Chip
            label="ON SCENE"
            color="warning"
            sx={{
              position: "absolute",
              top: 16,
              left: leftOffset,
              zIndex: 1000,
              fontWeight: 700,
              transition: "left 0.2s ease-in-out",
            }}
          />
        )}

        {/* Active Units Panel - Right Side (below incidents panel) */}
        <Paper
          elevation={6}
          sx={{
            position: "absolute",
            top: incidentListExpanded ? 360 : 160,
            right: 16,
            zIndex: 998,
            minWidth: tabletMode ? 300 : 240,
            maxWidth: tabletMode ? 400 : 320,
            maxHeight: "calc(100vh - 400px)",
            backgroundColor: theme.palette.mode === "dark" ? "rgba(18, 18, 18, 0.95)" : "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(8px)",
            borderRadius: 2,
            overflow: "hidden",
            transition: "top 0.3s ease-in-out",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: tabletMode ? 2.5 : 2,
              py: tabletMode ? 1.5 : 1,
              backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[700],
              color: theme.palette.common.white,
              cursor: "pointer",
            }}
            onClick={() => setUnitsListExpanded(!unitsListExpanded)}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <PeopleIcon fontSize={tabletMode ? "medium" : "small"} />
              <Typography variant={tabletMode ? "subtitle1" : "subtitle2"} sx={{ fontWeight: 700, textTransform: "uppercase" }}>
                Units ({activeUnitsForList?.length || 0})
              </Typography>
            </Stack>
            <IconButton size={tabletMode ? "medium" : "small"} sx={{ color: "inherit" }}>
              {unitsListExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={unitsListExpanded}>
            <Box sx={{ maxHeight: "40vh", overflowY: "auto" }}>
              {(!activeUnitsForList || activeUnitsForList.length === 0) ? (
                <Typography variant="body2" sx={{ p: 2, textAlign: "center", opacity: 0.7 }}>
                  No units in service
                </Typography>
              ) : (
                <Stack spacing={tabletMode ? 1 : 0.5} sx={{ p: tabletMode ? 1.5 : 1 }}>
                  {activeUnitsForList.map((unit) => (
                    <Paper
                      key={unit.id}
                      variant="outlined"
                      sx={{
                        p: 1,
                        opacity: unit.isStale ? 0.6 : 1,
                        ...(unit.needsStatusCheck && {
                          borderColor: theme.palette.warning.main,
                          borderWidth: 2,
                        }),
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase" }}>
                          {unit.id}
                        </Typography>
                        <Chip
                          label={unit.displayStatus || unit.status}
                          size="small"
                          color={
                            (unit.displayStatus || unit.status || "").toUpperCase() === "AVAILABLE" ? "success"
                            : (unit.displayStatus || unit.status || "").toUpperCase() === "BUSY" || (unit.displayStatus || unit.status || "").toUpperCase().includes("ON SCENE") ? "warning"
                            : (unit.displayStatus || unit.status || "").toUpperCase() === "EMERGENCY" || (unit.displayStatus || unit.status || "").toUpperCase() === "CHECK STATUS" ? "error"
                            : "default"
                          }
                          sx={{ fontWeight: 700, fontSize: 10, height: 20 }}
                        />
                      </Stack>
                      {unit.displayInfo && unit.displayInfo !== unit.displayStatus && (
                        <Typography variant="caption" sx={{ display: "block", opacity: 0.7, fontSize: 10, mt: 0.25 }}>
                          {unit.displayInfo}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>
          </Collapse>
        </Paper>

        {/* Enroute Indicator - Bottom Left (offset by sidebar) */}
        {userEnroute && avlMapDestination && (
          <Paper
            elevation={6}
            sx={{
              position: "absolute",
              bottom: 24,
              left: leftOffset,
              zIndex: 1000,
              px: 2,
              py: 1,
              backgroundColor: theme.palette.warning.main,
              color: theme.palette.warning.contrastText,
              borderRadius: 2,
              transition: "left 0.2s ease-in-out",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: "uppercase" }}>
              🚗 ENROUTE TO {avlMapDestination.cadId || `#${avlMapDestination.id}`}
            </Typography>
          </Paper>
        )}
      </Box>
    </Box>
  );
}

export default MapCenteredDashboard;
